import React from 'react';
import clsx from 'clsx';

import IconTradeUp from '@/assets/icons/icon-trade-up.svg?react';
import IconTradeDown from '@/assets/icons/icon-trade-down.svg?react';

interface AnalyticsMetricCardProps {
  label: string;
  value: number | null;
  previousValue: number | null;
  periodLabel?: string;
  formatValue?: (value: number) => string;
  formatDelta?: (absDelta: number) => string;
  invertTrend?: boolean;
  isActive?: boolean;
  className?: string;
}

const getTrendColor = (isUp: boolean, invertTrend: boolean): string => {
  const isPositive = invertTrend ? !isUp : isUp;
  return isPositive ? 'text-green-500' : 'text-coral-500';
};

export const AnalyticsMetricCard: React.FC<AnalyticsMetricCardProps> = ({
  label,
  value,
  previousValue,
  periodLabel = 'vs previous 30 days',
  formatValue,
  formatDelta,
  invertTrend = false,
  isActive: isActiveProp,
  className,
}) => {
  const hasData = value != null && previousValue != null;
  const isActive = isActiveProp ?? hasData;

  const rawDelta = hasData ? value - previousValue : 0;
  const isUp = rawDelta >= 0;
  const absDelta = Math.abs(rawDelta);
  const trendPercent =
    hasData && previousValue !== 0 ? ((absDelta / Math.abs(previousValue)) * 100).toFixed(1) : '0';

  const displayValue =
    value != null ? (formatValue ? formatValue(value) : value.toLocaleString()) : '-';
  const displayDelta = hasData
    ? formatDelta
      ? formatDelta(absDelta)
      : absDelta.toLocaleString()
    : '-';

  const trendColor = getTrendColor(isUp, invertTrend);

  return (
    <div
      className={clsx(
        'flex min-w-0 flex-col items-start justify-center gap-2 overflow-hidden rounded-xl py-3 pr-3 pl-4 shadow-[0px_4px_12px_0px_rgba(20,27,36,0.04)] outline-1 -outline-offset-1 outline-neutral-100',
        'bg-neutral-0',
        !isActive && 'opacity-50',
        className,
      )}
    >
      <div className="flex flex-col items-start justify-start gap-2 self-stretch">
        <div className="text-xs leading-4 font-medium text-neutral-500">{label}</div>

        <div
          className={clsx(
            'inline-flex h-6 items-center justify-center gap-0.5 rounded-lg bg-neutral-50 p-1.5 outline-1 -outline-offset-1 outline-neutral-100',
          )}
        >
          <div className={clsx('text-[10.29px] leading-3 font-medium', trendColor)}>
            {isUp ? '+' : ''}
            {trendPercent}%
          </div>
          <div className={clsx('relative h-3.5 w-3.5 overflow-hidden', trendColor)}>
            {isUp ? (
              <IconTradeUp className="h-full w-full" />
            ) : (
              <IconTradeDown className="h-full w-full" />
            )}
          </div>
        </div>
      </div>

      <div className="self-stretch text-2xl font-semibold text-neutral-900">{displayValue}</div>

      <div className="flex flex-col items-start justify-center self-stretch">
        <div className="inline-flex items-center justify-start gap-1">
          <div className={clsx('text-[10px] leading-5 font-semibold', trendColor)}>
            {hasData ? (isUp ? '+' : '-') : ''}
            {displayDelta}
          </div>
        </div>
        <div className="inline-flex items-center justify-start gap-1">
          <div className="text-[10px] leading-4 font-medium text-neutral-400">{periodLabel}</div>
        </div>
      </div>
    </div>
  );
};
